import type { Doc } from "building-types/doc";
import { OptionStateInfiniteLoopError } from "building-types/errors/buildingGenerationError";
import { parseInstanceOptionSetId } from "building-types/options/instanceOptionSetId";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { DocOption, DocOptionLock, DocOptionSet, IntersectingOptionSet } from "building-types/options/optionSet";
import type { OptionSetEntityDisconnectedTable } from "building-types/options/optionSetEntityRelationsTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import objectIsEmpty from "common/utils/objectIsEmpty";
import shallowCompare from "common/utils/shallowCompare";
import type { Mutable } from "common/utils/types";
import type { Value } from "expr-eval";
import { docGetActiveOptionTemplateOverrides } from "../docGetOptionTemplates";
import { parseFormula } from "../formula/formulaParsing";
import { docToOptionDefinitions } from "./docGetInstanceOptionSets";
import { getOptionSetArchetypeOrSelf } from "./getOptionSetArchetypeOrSelf";
import { getOptionLockByOptionSetId } from "./optionLocksTools";
import { isBooleanOptionSet, optionSetDefaultOptionId } from "./optionSetBaseUtils";
import { removeOrphanedInteriorPaletteOptionSet } from "./paletteOptionSet";

export function isIntersectingOptionSet(optionSet: DocOptionSet): optionSet is IntersectingOptionSet {
  return optionSet.optionSetType === "INTERSECTING";
}

/**
 * Computes the diff between current and previous option choices.
 * Returns the changed choices, or `null` when there are no changes (caller can skip processing).
 */
export function getActiveChoices(currentChoices: OptionChoices, previousChoices: OptionChoices): OptionChoices | null {
  const diff: Mutable<OptionChoices> = {};

  for (const key of Object.keys(currentChoices)) {
    if (currentChoices[key] !== previousChoices[key]) {
      diff[key] = currentChoices[key];
    }
  }

  for (const key of Object.keys(previousChoices)) {
    if (!(key in currentChoices)) {
      diff[key] = currentChoices[key];
    }
  }

  if (objectIsEmpty(diff)) {
    return null;
  }

  return diff;
}

/**
 * Performs some validation and sanitization of the "optionSets" and "optionChoices" tables
 * of a Doc. Prints warnings to console if any validation errors are found.
 *
 * @param {Doc} doc The Studio Document to validate
 *
 * @returns {Doc} The sanitized Studio Document
 */
export function sanitizeDocOptionSetsAndOptionChoices(doc: Doc): Doc {
  const { optionChoices, optionSets } = doc;

  const newOptionChoices: Mutable<OptionChoices> = optionChoices ? { ...optionChoices } : {}; // Sanitize optionChoices and apply overrides

  const cleanedOptionSets = removeOrphanedInteriorPaletteOptionSet(optionSets);

  for (const [optionSetId, optionSet] of Object.entries(cleanedOptionSets)) {
    if (optionSet?.options && !optionSet.disabled && !optionSet.readonly) {
      let currentChoice = optionChoices?.[optionSetId];
      let defaultChoice = optionSetDefaultOptionId(optionSet, cleanedOptionSets);

      const currentOption = currentChoice !== null ? optionSet.options[currentChoice] : undefined;
      const defaultOption = defaultChoice !== null ? optionSet.options[defaultChoice] : undefined;

      // Option set must have a defaultValue defined
      if (!(defaultOption !== undefined)) {
        console.assert(false, `Default option for option set ${optionSetId} is undefined!`);
      }

      if (isBooleanOptionSet(optionSet)) {
        if (typeof currentChoice === "boolean") {
          currentChoice = currentChoice ? "true" : "false";
        }
        if (typeof defaultChoice === "boolean") {
          defaultChoice = defaultChoice ? "true" : "false";
        }
        if (!(currentChoice === "true" || currentChoice === "false" || currentChoice === undefined)) {
          console.assert(false, `currentChoice is not 'true' or 'false' or undefined! Value of currentChoice (${typeof currentChoice}): ${currentChoice}`);
        }
        if (!(defaultChoice === "true" || defaultChoice === "false")) {
          console.assert(false, `defaultChoice is not 'true' or 'false'! Value of defaultChoice (${typeof defaultChoice}): ${defaultChoice}`);
        }

        if (currentChoice === "true" || defaultChoice === "true") {
          newOptionChoices[optionSetId] = "true"; // checked or the default is checked
        }
      } else {
        if (currentOption && currentOption !== defaultOption && !currentOption.disabled) {
          newOptionChoices[optionSetId] = currentChoice; // it's enabled and isn't a base option
        }
      }
    }
  }

  return {
    ...doc,
    optionChoices: newOptionChoices,
    optionSets: cleanedOptionSets,
  };
}

export type DocOptionSanitizeDefinitions = OptionDefinitions &
  Pick<Doc, "optionTemplates" | "optionSetOverrides" | "optionLocks">;

/**
 * Builds the definitions object required by `sanitizeOptionChoices` from a full doc.
 */
export function docOptionSanitizeDefinitions(doc: Doc): DocOptionSanitizeDefinitions {
  return { ...doc, ...docToOptionDefinitions(doc) };
}

/**
 * Builds the definitions object when option sets are provided separately from the doc
 * (e.g. evaluated building option sets).
 */
export function docOptionSanitizeDefinitionsFromSets(
  doc: Pick<Doc, "optionTemplates" | "optionSetOverrides" | "optionLocks" | "instanceOptionSets">,
  optionSets: Table<DocOptionSet>,
): DocOptionSanitizeDefinitions {
  return { ...doc, optionSets };
}

/**
 * Sanitizes option choices using a full doc (templates, overrides, locks, and option definitions).
 */
export function sanitizeDocOptionChoices(
  doc: Doc,
  optionChoices: OptionChoices | null = {},
  disconnectedOptionSets: OptionSetEntityDisconnectedTable = {},
  maxIterations: number = 10,
): OptionChoices {
  return sanitizeOptionChoices(docOptionSanitizeDefinitions(doc), optionChoices, disconnectedOptionSets, maxIterations);
}

/**
 * Sanitizes option choices when option sets are supplied separately from the doc
 * (e.g. evaluated building option sets).
 */
export function sanitizeDocOptionChoicesFromSets(
  doc: Pick<Doc, "optionTemplates" | "optionSetOverrides" | "optionLocks" | "instanceOptionSets">,
  optionSets: Table<DocOptionSet>,
  optionChoices: OptionChoices | null = {},
  disconnectedOptionSets: OptionSetEntityDisconnectedTable = {},
  maxIterations: number = 10,
): OptionChoices {
  return sanitizeOptionChoices(
    docOptionSanitizeDefinitionsFromSets(doc, optionSets),
    optionChoices,
    disconnectedOptionSets,
    maxIterations,
  );
}

/**
 * sanitizeOptionChoices - "sanitizes" the optionChoices object by removing any choices that are not
 * valid.
 *
 * NOTE: Used in server and client code, therefore expects a parsed Studio Document. Do not
 * call docMarshal inside this function, as there are issues with sending TypedArray objects over
 * the wire.
 */
export function sanitizeOptionChoices(
  definitions: DocOptionSanitizeDefinitions,
  optionChoices: OptionChoices | null = {},
  disconnectedOptionSets: OptionSetEntityDisconnectedTable = {},
  maxIterations: number = 10,
) {
  if (!optionChoices) {
    optionChoices = {};
  }

  let prevSanitizedOptionChoices: OptionChoices = {};
  let sanitizedOptionChoices = optionChoices;

  let iterations = 0;
  while (!shallowCompare(sanitizedOptionChoices, prevSanitizedOptionChoices)) {
    prevSanitizedOptionChoices = sanitizedOptionChoices;
    sanitizedOptionChoices = sanitizeOptionChoicesStep(definitions, prevSanitizedOptionChoices, disconnectedOptionSets);

    iterations++;
    if (iterations > maxIterations) {
      throw new OptionStateInfiniteLoopError();
    }
  }

  return sanitizedOptionChoices;
}

function sanitizeOptionChoicesStep(
  definitions: DocOptionSanitizeDefinitions,
  optionChoices: OptionChoices | null = {},
  disconnectedOptionSets: OptionSetEntityDisconnectedTable = {},
) {
  if (!optionChoices) {
    optionChoices = {};
  }

  const { optionSets } = definitions;
  const optionSetOverride = docGetActiveOptionTemplateOverrides(definitions) ?? {};
  const goodOptionChoices = {
    ...optionChoices,
  };
  const baseOptionChoices: Mutable<OptionChoices> = {};

  // Materialize archetype defaults so the sanitization loop below (and the lock-condition
  // formulas it evaluates) sees a real value at every archetype id.
  // Instance defaults are NOT materialized — they resolve to the archetype default at read time via `optionSetSelectedOptionId`.
  for (const [optionSetId, optionSet] of Object.entries(optionSets)) {
    if (optionChoices[optionSetId] === undefined) {
      baseOptionChoices[optionSetId] = optionSetDefaultOptionId(optionSet, optionSets);
    }
  }

  const allOptionChoices = { ...optionChoices, ...baseOptionChoices };

  const isOptionSetLocked = (optionLock: DocOptionLock | undefined, optionSetId: string): boolean => {
    let optionSetLocked = false;

    try {
      const condition = // force lock condition for option
        optionSetOverride[optionSetId]?.disconnectCondition /* override has precedence */ ??
        optionLock?.disconnectCondition; /* inherited is the fallback */
      if (condition && !parseFormula(condition.formula).evaluate(allOptionChoices as Value)) {
        optionSetLocked = true;
      }
    } catch (e) {
      // ignore force lock because condition.formula is invalid
    }

    return optionSetLocked;
  };

  const isOptionLocked = (
    optionLock: DocOptionLock | undefined,
    optionSet: DocOptionSet,
    optionId: string | null,
  ): boolean => {
    let optionLocked = false;

    try {
      const condition = // force lock condition for option
        optionId !== null
          ? optionSetOverride[optionSet.id]?.disconnectConditionOptions?.[optionId] /* override has precedence */ ??
            optionLock?.disconnectConditionOptions?.[optionId]
          : undefined; /* inherited is the fallback */
      if (condition && !parseFormula(condition.formula).evaluate(allOptionChoices as Value)) {
        optionLocked = true;
      }
    } catch (e) {
      // ignore force lock because condition.formula is invalid
    }

    return optionLocked;
  };

  const optionSetIdsWithOverriddenDefaults = Object.entries(optionSetOverride).reduce<string[]>(
    (acc, [optionSetId, override]) => {
      const optionSet = optionSets[optionSetId];
      const overrideDefault = "defaultValue" in override ? override.defaultValue : undefined;
      if (optionSet && overrideDefault && overrideDefault !== optionSetDefaultOptionId(optionSet, optionSets)) {
        acc.push(optionSetId);
      }
      return acc;
    },
    [],
  );

  for (const [optionSetId, optionId] of Object.entries(allOptionChoices)) {
    // For instance ids, resolve to the archetype for use in validation and default deduplication.
    const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);

    const option = optionId !== null ? optionSet?.options?.[optionId] : undefined;

    let chooseBaseOption = false;
    let baseOptionLocked = false;

    // Introduced in migration 0.0.415
    const optionLock = getOptionLockByOptionSetId(definitions.optionLocks, optionSetId);
    const optionSetLocked = isOptionSetLocked(optionLock, optionSetId);

    const optionSetDefault = optionSet ? optionSetDefaultOptionId(optionSet, optionSets) : null;

    if (!optionSet) {
      delete goodOptionChoices[optionSetId]; // option set doesn't exist so you shouldn't be able choose any option in it
    } else if (!option) {
      chooseBaseOption = true; // option unavailable because it doesn't exist in parent option set
    } else if (optionSet.disabled) {
      chooseBaseOption = true; // option unavailable because parent option set is disabled
    } else if (optionSet.readonly) {
      chooseBaseOption = true; // readonly sets are locked to the resolved default
    } else if (option.disabled) {
      chooseBaseOption = true; // option unavailable because option is disabled
    } else if (!!disconnectedOptionSets[optionSetId]) {
      chooseBaseOption = true; // option unavailable because option set has no effect on the floor plan so was programmatically locked
    } else if (optionId === optionSetDefault) {
      // A choice matching the declared default can normally be omitted. If an active template
      // changes the effective default, omitting that choice would select the template default
      // instead, so preserve it. Template overrides are keyed by regular/archetype option set id,
      // so an instance checks its archetype id.
      const parsedInstanceId = parseInstanceOptionSetId(optionSetId);
      const overrideCheckKey = parsedInstanceId?.archetypeId ?? optionSetId;
      if (!optionSetIdsWithOverriddenDefaults.includes(overrideCheckKey)) {
        chooseBaseOption = true; // base option was explicit choice
        baseOptionLocked = isOptionLocked(optionLock, optionSet, optionId);
      }
    } else {
      // options locked programmatically need not be removed because they have no effect on the floor plan (that's why they're locked)
      // however force locked options need removal because they could incorrectly change the floor plan if fed to building generation
      if (optionSetLocked) {
        chooseBaseOption = true; // option set was force locked so choose base
      }

      if (isOptionLocked(optionLock, optionSet, optionId)) {
        chooseBaseOption = true; // option was force locked so choose base option if possible
        baseOptionLocked = optionId === optionSetDefault; // must choose alternative to base option
      }
    }

    if (chooseBaseOption) {
      // chooseBaseOption is only set true in branches that require optionSet to be defined.
      assertIsNotNullOrUndefined(optionSet);
      delete goodOptionChoices[optionSetId]; // no explicit choice implicitly chooses base option for almost all option sets

      if (isBooleanOptionSet(optionSet) && optionSetDefault === "true") {
        goodOptionChoices[optionSetId] = "true"; // handle edge case of of base option being 'true' instead of 'false'
      }

      if (baseOptionLocked && !optionSetLocked) {
        for (const nonBaseId of Object.keys(optionSet.options).filter((x) => x !== optionSetDefault)) {
          if (!isOptionLocked(optionLock, optionSet, nonBaseId) && !optionSet.options[nonBaseId]?.disabled) {
            goodOptionChoices[optionSetId] = nonBaseId; // alternative to locked base option
            break;
          }
        }
      }
    }
  }

  return goodOptionChoices;
}

interface OptionSetWithId {
  optionSetId?: string;
  optionSet?: DocOptionSet;
  optionId?: string;
  option?: DocOption;
}

interface OptionWithId {
  optionId?: string;
  option?: DocOption;
}

export function findOptionSetByOptionCode(optionSets: Doc["optionSets"], optionCode: string): OptionSetWithId {
  let optionId: string | undefined;
  let option: DocOption | undefined;

  const optionSetId = Object.keys(optionSets).find((id) => {
    for (const optionKey in optionSets[id].options) {
      if (
        optionSets[id].options[optionKey].builderOptionCode === optionCode ||
        optionKey === optionCode ||
        optionKey.startsWith(`option_${optionCode}`) ||
        optionCode.startsWith(`option_${optionKey}`)
      ) {
        optionId = optionKey;
        option = optionSets[id].options[optionId];

        return true;
      }
    }
    return undefined;
  });

  return {
    optionSetId,
    optionSet: optionSetId !== undefined ? optionSets[optionSetId] : undefined,
    optionId,
    option,
  };
}

export function findOptionSetByCode(optionSets: Doc["optionSets"], optionSetCode: string): OptionSetWithId {
  const optionSetId = Object.keys(optionSets).find(
    (id) =>
      optionSets[id].builderOptionSetCode === optionSetCode ||
      id === optionSetCode ||
      id.startsWith(`optionSet_${optionSetCode}`) ||
      optionSetCode.startsWith(`optionSet_${id}`),
  );

  return {
    optionSetId,
    optionSet: optionSetId !== undefined ? optionSets[optionSetId] : undefined,
  };
}

export function findOptionByCode(optionSet: DocOptionSet, optionCode: string): OptionWithId {
  const optionId = Object.keys(optionSet?.options).find(
    (id) =>
      optionSet.options[id]?.builderOptionCode === optionCode ||
      id === optionCode ||
      id.startsWith(`option_${optionCode}`) ||
      optionCode.startsWith(`option_${id}`),
  );

  return {
    optionId,
    option: optionId !== undefined ? optionSet.options[optionId] : undefined,
  };
}

/**
 * Maps an OptionChoices object to one that uses the builderOptionSetCode and builderOptionCode values
 * instead of our internal IDs, meant for consumption by external integrations with Higharc.
 *
 * Falls back to our internal IDs when the builder equivalent is not present.
 */
export function mapOptionChoicesToBuilderOptionChoices(optionSets: Doc["optionSets"], optionChoices: OptionChoices) {
  const mappedOptionChoices: Mutable<OptionChoices> = {};

  for (const optionSetId in optionChoices) {
    const optionId = optionChoices[optionSetId];
    const optionSet = optionSets[optionSetId];
    const optionSetCode = optionSet?.builderOptionSetCode || optionSetId;
    const option = optionId !== null ? optionSet?.options?.[optionId] : undefined;
    const optionCode = option?.builderOptionCode || optionId;

    mappedOptionChoices[optionSetCode] = optionCode;
  }

  return mappedOptionChoices;
}

/**
 * Inverse of mapOptionChoicesToBuilderOptionChoices
 *
 * NOTE - Supports use of both builder-provided IDs and internal IDs from the Studio Document.
 * Works with interior and exterior palette options.
 */
export function mapBuilderOptionChoicesToOptionChoices(optionSets: Doc["optionSets"], optionChoices: OptionChoices) {
  const mappedOptionChoices: Mutable<OptionChoices> = {};

  for (const optionSetCode in optionChoices) {
    const optionCode = optionChoices[optionSetCode];
    const { optionSetId, optionSet } = findOptionSetByCode(optionSets, optionSetCode);

    if (optionCode !== null && optionSet && optionSetId) {
      const { optionId, option } = findOptionByCode(optionSet, optionCode);

      if (option && optionId !== undefined && optionSetId !== undefined) {
        mappedOptionChoices[optionSetId] = optionId;
      }
    }
  }

  return mappedOptionChoices;
}

const defaultsValuesCache = new WeakMap<Table<DocOptionSet>, OptionChoices>();

/**
 * Default option id per option set. Cached by table identity — `optionSets` must be
 * treated as immutable (replace the table on mutation; do not delete/add keys in place).
 */
export function getDefaultsValues(optionSets: Table<DocOptionSet>): OptionChoices {
  const cached = defaultsValuesCache.get(optionSets);
  if (cached) {
    return cached;
  }

  const defaults: OptionChoices = Object.fromEntries(
    Object.entries(optionSets)
      .filter(([, optionSet]) => !isIntersectingOptionSet(optionSet))
      .map(([optionSetId, optionSet]) => [optionSetId, optionSetDefaultOptionId(optionSet, optionSets)]),
  );
  defaultsValuesCache.set(optionSets, defaults);
  return defaults;
}

export function cleanOptionChoices(definitions: OptionDefinitions, optionChoices: OptionChoices): OptionChoices {
  return Object.fromEntries(
    Object.entries(optionChoices ?? {}).filter(([optionSetId, optionId]) => {
      if (typeof optionId !== "string") {
        return false;
      }
      const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);
      return optionSet?.options[optionId] && !isIntersectingOptionSet(optionSet);
    }),
  );
}

export function getOptionChoicesWithDefaults(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices = {},
): OptionChoices {
  return {
    ...getDefaultsValues(definitions.optionSets),
    ...cleanOptionChoices(definitions, optionChoices),
  };
}

/**
 * This is not a particularly safe function to use, as it assumes that the optionId is unique across all optionSets.
 * This is fairly unlikely as boolean option sets use ids like "true" or "false". You should essentially always qualify
 * with the optionset id.
 */
export function optionChoiceById(optionSets: Table<DocOptionSet>, optionId: string): DocOption | null {
  for (const id in optionSets) {
    const optionSet = optionSets[id];
    if (optionSet.options[optionId]) {
      return optionSet.options[optionId];
    }
  }
  return null;
}

export function optionChoiceBySetAndChoiceId(
  optionSets: Table<DocOptionSet>,
  optionSetId: string,
  optionId: string,
): DocOption | null {
  const optionSet = optionSets[optionSetId];
  if (optionSet?.options[optionId]) {
    return optionSet.options[optionId];
  }

  return null;
}
